import { TsDOM as q } from '@/scripts/core/TsDOM'
import { UcoastEl } from '@/scripts/core/UcoastEl'

export class DetailsModal extends UcoastEl {
	static htmlSelector = 'details-modal'
	static openAttribute = 'data-uc-modal-open'
	detailsContainer: HTMLDetailsElement
	summaryToggle: HTMLElement
	onBodyClickEvent?: (event: MouseEvent) => undefined
	closeAnimationCleanup?: () => void
	constructor() {
		super()
		this.detailsContainer = q.rs('details', this)
		this.summaryToggle = q.rs('summary', this)

		this.detailsContainer.addEventListener(
			'keyup',
			(event) => event.code?.toUpperCase() === 'ESCAPE' && this.close()
		)
		this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this))
		q.rl<HTMLButtonElement>('.modal__close-button', this).forEach((button) =>
			button.addEventListener('click', this.close.bind(this))
		)

		this.summaryToggle.setAttribute('role', 'button')
	}

	isOpen() {
		return this.detailsContainer.hasAttribute('open')
	}

	onSummaryClick(event: MouseEvent) {
		event.preventDefault()
		const closestDetails = q.oClosestTarget(event, 'details')
		closestDetails?.hasAttribute('open')
			? this.close()
			: this.open({
					target: q.rt(event),
			  })
	}

	onBodyClick(event: MouseEvent) {
		const target = q.rt(event)
		if (!this.contains(target) || target.classList.contains('modal-overlay')) this.close(false)
	}

	open(event: { target: HTMLElement }) {
		this.cancelPendingClose()
		this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this)
		if (!this.onBodyClickEvent) throw new Error('onBodyClickEvent is undefined')
		const closestDetails = q.oc(event.target, 'details')
		if (closestDetails) {
			closestDetails.setAttribute('open', 'true')
		}
		const animatedContent = q.os<HTMLElement>(
			':scope > .search-modal',
			this.detailsContainer
		)
		void animatedContent?.offsetWidth
		this.detailsContainer.setAttribute(DetailsModal.openAttribute, '')
		window.TsDOM.trapFocus(
			q.rs('[tabindex="-1"]', this.detailsContainer),
			q.os('input:not([type="hidden"])', this.detailsContainer)
		)

		document.body.addEventListener('click', this.onBodyClickEvent)
		document.body.classList.add('overflow-hidden')
	}

	close(focusToggle = true) {
		if (!this.isOpen() || this.closeAnimationCleanup) return
		this.detailsContainer.removeAttribute(DetailsModal.openAttribute)

		const animatedContent = q.os<HTMLElement>(
			':scope > .search-modal',
			this.detailsContainer
		)
		if (!animatedContent) {
			this.finishClose(focusToggle)
			return
		}

		const expectedProperty = window.matchMedia(
			'(prefers-reduced-motion: reduce)'
		).matches
			? 'opacity'
			: 'transform'
		const handleTransitionEnd = (event: TransitionEvent) => {
			if (
				event.target !== animatedContent ||
				event.propertyName !== expectedProperty
			)
				return
			this.finishClose(focusToggle)
		}
		animatedContent.addEventListener(
			'transitionend',
			handleTransitionEnd
		)
		const fallback = window.setTimeout(() => this.finishClose(focusToggle), 400)
		this.closeAnimationCleanup = () => {
			animatedContent.removeEventListener('transitionend', handleTransitionEnd)
			window.clearTimeout(fallback)
			this.closeAnimationCleanup = undefined
		}
	}

	finishClose(focusToggle: boolean) {
		this.cancelPendingClose()
		window.TsDOM.removeTrapFocus(focusToggle ? this.summaryToggle : undefined)
		this.detailsContainer.removeAttribute('open')
		this.detailsContainer.removeAttribute(DetailsModal.openAttribute)
		if (this.onBodyClickEvent) {
			document.body.removeEventListener('click', this.onBodyClickEvent)
		}

		document.body.classList.remove('overflow-hidden')
	}

	cancelPendingClose() {
		this.closeAnimationCleanup?.()
	}

	override disconnectedCallback() {
		if (this.isOpen()) this.finishClose(false)
		else {
			this.cancelPendingClose()
			if (this.onBodyClickEvent)
				document.body.removeEventListener('click', this.onBodyClickEvent)
		}
		super.disconnectedCallback()
	}
}

customElements.define('details-modal', DetailsModal)
